import React, { useState, useEffect, useRef } from "react";
import {
  Grid,
  TextField,
  FormControlLabel,
  Checkbox,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
  Card,
  CardContent,
  CardHeader,
  InputAdornment,
  Link,
} from "@mui/material";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { getClient } from "../API/clientAPI";
import { putJobOrder, getJobOrder, getJobOrders } from "../API/jobOrderAPI";
import { getTechnicians } from "../API/technicianAPI";
import { createTheme } from "@mui/material/styles";
import { styled } from "@mui/material/styles";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import BaseFormWrapper from "../../components/shared/BaseFormWrapper";
import { s3TempBucketUploader, TEMP_BUCKET_NAME } from "../Promotions/config/aws-config-temp-bucket";
import SignatureCanvas from "react-signature-canvas";
import { submitDocumentFieldMapper } from "../API/documentFieldMapperAPI";
import { sendEmailQueue } from "../API/emailAPI";


const theme = createTheme({
  palette: {
    primary: {
      main: "#1e5d45", // Custom primary color
    },
  },
});

const StyledHr = styled("hr")(({ theme }) => ({
  border: 0,
  borderTop: `1px solid ${theme.palette.divider}`,
  // margin: theme.spacing(4, 0),
  opacity: 0.8,
}));

function EditServiceReport() {
  const { id } = useParams();
  const [jobOrders, setJobOrders] = useState([]);
  const [error, setError] = useState(null);
  const [jobOrder, setJobOrder] = useState({});
  const [inspectionReport, setInspectionReport] = useState([]);

  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const returnTo = searchParams.get('returnTo');

  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [client, setClient] = useState({});
  const [editedClient, setEditedClient] = useState({});
  const [acknowledgedSignatureURL, setAcknowledgedSignatureURL] = useState("");
  const [accomplishedSignatureURL, setAccomplishedSignatureURL] = useState("");
  const acknowledgedSignatureRef = useRef(null);
  const accomplishedSignatureRef = useRef(null);
  const [jobOrderParameters, setjobOrderParameters] = useState({
    status: "",
    appointment_type: "",
    treatment_type: "",
    pest_problem: "",
    inspection_report: [],
    services: [],
    chemical_used: [],
    equipment_used: [],
    service_fee: "",
    service_report_payment_status: [],
    service_report_payment_method: [],
    job_date: "",
    start_time: "",
    end_time: "",
    accomplished_by_technician: "",
  });

  const [chemical_used, setChemicalUsed] = useState([]);
  const [equipment_used, setEquipmentUsed] = useState([]);
  const [service_report_payment_status, setServiceReportPaymentStatus] =
    useState([]);
  const [service_report_payment_method, setServiceReportPaymentMethod] = useState([]);
  const [reminder_notes, setReminderNotes] = useState("");
  const [service_fee, setServiceFee] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const convertBooleans = (obj) => {
    if (Array.isArray(obj)) {
      return obj.map((item) => convertBooleans(item));
    } else if (obj && typeof obj === "object") {
      return Object.fromEntries(
        Object.entries(obj).map(([key, value]) => [key, convertBooleans(value)])
      );
    } else if (typeof obj === "boolean") {
      return obj ? "True" : "False"; // Convert true/false to 'True'/'False'
    }
    return obj;
  };


  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);
        const data = await getJobOrder(id);
        setJobOrder(data);
        setAcknowledgedSignatureURL(data.acknowledgedSignature || "");
        setAccomplishedSignatureURL(data.accomplishedSignature || "");
        const client = await getClient(data.client_id);
        setClient(client);

        setjobOrderParameters((prev) => ({
          ...prev,
          status: data?.status || "",
          appointment_type: data?.appointment_type || "",
          treatment_type: data?.treatment_type || "",
          job_date: data?.job_date || "",
          start_time: data?.start_time || "",
          end_time: data?.end_time || "",
          service_report_payment_status:
            data?.service_report_payment_status || [],
          accomplished_by_technician: data?.accomplished_by_technician,
        }));

        setChemicalUsed(
          data?.chemical_used?.length
            ? data.chemical_used
            : [
              { label: "Spraying", chemical: "", dilution: "" },
              { label: "Misting", chemical: "", dilution: "" },
              { label: "Trapping", chemical: "", dilution: "" },
              { label: "Rodent Bait", chemical: "", dilution: "" },
              { label: "Gel Bait", chemical: "", dilution: "" },
              { label: "Others", chemical: "", dilution: "" },
            ]
        );

        setEquipmentUsed(
          data?.equipment_used?.length
            ? data.equipment_used
            : [
              { label: "Sprayer", usage: false },
              { label: "Misting Machine", usage: false },
              { label: "Fogging Machine", usage: false },
              { label: "Power Sprayer", usage: false },
              { label: "Others", usage: false },
            ]
        );

        setServiceReportPaymentStatus(
          data?.service_report_payment_status?.length
            ? data.service_report_payment_status
            : [
              { label: "Paid", selection: false },
              { label: "COD", selection: false },
              { label: "For Collection", selection: false },
            ]
        );

        setServiceReportPaymentMethod(
          data?.service_report_payment_method?.length
            ? data.service_report_payment_method
            : [
              { label: "Cash", selection: false },
              { label: "Check", selection: false },
              { label: "Free", selection: false },
            ]
        );

        setInspectionReport(data?.inspection_report || []);
        setEditedClient(data.client);

        // Initialize reminder_notes and service_fee
        setReminderNotes(data?.reminder_notes || "");
        setServiceFee(data?.service_fee || "");
      } catch (err) {
        setErrorMessage("Error fetching job order");
        console.error("Error fetching job order:", err);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [id]);

  const saveSignature = async (ref, type) => {
    if (ref.current.isEmpty()) {
      setErrorMessage("Please provide a signature before saving.");
      return;
    }
    const canvas = ref.current.getTrimmedCanvas();
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
    const fileName = `${id}-signature-${type}.png`;

    const uploadParams = {
      Bucket: TEMP_BUCKET_NAME,
      Key: fileName,
      Body: blob,
      ContentType: "image/png",
    };

    try {
      await s3TempBucketUploader.upload(uploadParams).promise();
      const newURL = `https://${TEMP_BUCKET_NAME}.s3.amazonaws.com/${fileName}`;

      // Update the corresponding state
      if (type === "acknowledged") {
        setAcknowledgedSignatureURL(newURL);
      } else {
        setAccomplishedSignatureURL(newURL);
      }

      // Update job order in the database
      const updatedJobOrder = { ...jobOrder, [`${type}Signature`]: newURL };
      await putJobOrder(id, updatedJobOrder);
      setSuccessMessage("Signature saved successfully.");
    } catch (error) {
      setErrorMessage("Error saving signature. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const clearSignature = (ref, type) => {
    ref.current.clear();

  };

  const handleJobOrderChange = (field, value) => {
    setjobOrderParameters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const inspectionPestColumns = [
    "Roaches",
    "Mosq",
    "Rodents",
    "Flies",
    "Ants",
    "Others",
  ];
  const inspectionRows = Array.from({ length: 10 }, (_, index) => ({
    id: index + 1,
  })); // Example rows

  const competencies = [
    "General Pest Control",
    "Termite Control",
    "QPMS",
    "Disinfection",
    "Basic Safety",
    "Driving 2 Wheels",
    "Driving 4 Wheels",
  ];
  const specializationGPC = [
    "Rodent",
    "Baiting",
    "Cockroach",
    "Fly Control",
    "Ant Control",
    "Larviciding",
  ];

  const specializationTermite = [
    "Compre",
    "AG",
    "IG",
    "IC",
    "Spraying",
    "Soil Poisoning",
  ];

  const job_types = [
    "GPC",
    "Fogging",
    "Monitoring",
    "Deep Cleaning",
    "QTM",
    "Heat Treatment",
    "Vacuuming",
    "Termite Control",
    "Comprehensive Termite",
    "Termite Baiting",
    "Termite Spot Spraying",
    "AG Installation",
    "Phase 1 and Phase 2",
    "QPMS",
    "Ant Control",
    "Cockroach Control",
    "Mosquito Control",
    "Cockroach Gel Bait/Powder",
    "Basic Rodent Control",
    "Intensive Rodent Control",
    "Termite Inspection",
    "Disinfection",
    "Billing",
    "Collection",
    "Deliver Service Invoice",
    "Deliver Certificate",
    "QuickCert",
    "Ocular",
    "Rodent Monitoring",
    "Daily Monitoring",
    "Logistics",
    "ILT Monitoring",
    "Facility Audit",
    "Meeting/Seminar",
    "QA Visit",
    "Termipore Installation",
    "In2Care Installation",
    "Exterra Inground",
    "Delivery of Materials",
    "Backjob"
  ]

  const legendTable = [
    {
      finding: "1 Presence of foul odor",
      recommendation: "A Keep areas clean and dry",
    },
    {
      finding: "2 Trash bins left uncovered",
      recommendation: "B Keep trash bins covered",
    },
    {
      finding: "3 Dirt/Organic Matter Present",
      recommendation: "C Dispose Garbage Daily",
    },
    {
      finding: "4 Cracks and Crevices",
      recommendation: "D Seal / Caulking of Cracks and Crevices",
    },
    {
      finding: "5 Pest Entry Point / Open Windows",
      recommendation: "E Keep doors/windows closed",
    },
    {
      finding: "6 Stagnant Water / Leaking Pipes",
      recommendation: "F Fix leaking pipes and gutters",
    },
    {
      finding: "7 Build-up of Old Stocks",
      recommendation: "G Organize Piling and Storage",
    },
    {
      finding: "8 Closed Room or Area Inaccessible",
      recommendation: "H Make room available for inspection",
    },
    {
      finding: "9 Active termites found",
      recommendation: "I Install screens/weather strip",
    },
  ];

  const [services, setServices] = useState([
    {
      id: 1,
      activity: "Inspection",
      service: "Initial GPC",
      activityChecked: false,
      serviceChecked: false,
    },
    {
      id: 2,
      activity: "Monitoring",
      service: "Comprehensive GPC",
      activityChecked: false,
      serviceChecked: false,
    },
    {
      id: 3,
      activity: "Spraying",
      service: "Disinfection Treatment",
      activityChecked: false,
      serviceChecked: false,
    },
    {
      id: 4,
      activity: "Misting",
      service: "Exterra Colony Elimination",
      activityChecked: false,
      serviceChecked: false,
    },
    {
      id: 5,
      activity: "Gel Baiting",
      service: "Comprehensive Termite",
      activityChecked: false,
      serviceChecked: false,
    },
    {
      id: 6,
      activity: "Glue Trap",
      service: "Rodent Control",
      activityChecked: false,
      serviceChecked: false,
    },
    {
      id: 7,
      activity: "Rodent Bait",
      service: "Mosquito Control",
      activityChecked: false,
      serviceChecked: false,
    },
    {
      id: 8,
      activity: "Rat Cage",
      service: "Cockroach Control",
      activityChecked: false,
      serviceChecked: false,
    },
    {
      id: 9,
      activity: "Wiping",
      service: "Audit / Follow-up",
      activityChecked: false,
      serviceChecked: false,
    },
  ]);

  useEffect(() => {
    if (jobOrder.services) {
      setServices(
        jobOrder.services.map((service) => ({
          ...service,
          activityChecked: service.activityChecked || false,
          serviceChecked: service.serviceChecked || false,
        }))
      );
    }
  }, [jobOrder.services]);

  const handleServicesCheckboxChange = (id, checkboxType) => {
    setServices((prevServices) =>
      prevServices.map((service) =>
        service.id === id
          ? { ...service, [checkboxType]: !service[checkboxType] }
          : service
      )
    );
  };

  // Handler for payment status
  const handlePaymentStatusChange = (statusIndex) => {
    const newStatusOptions = service_report_payment_status.map(
      (status, index) => ({
        ...status,
        selection: index === statusIndex ? !status.selection : false,
      })
    );
    setServiceReportPaymentStatus(newStatusOptions);
  };

  // Handler for payment method
  const handlePaymentMethodChange = (methodIndex) => {
    const newMethodOptions = service_report_payment_method.map((method, index) => ({
      ...method,
      selection: index === methodIndex ? !method.selection : false,
    }));
    setServiceReportPaymentMethod(newMethodOptions);
  };

  const [technicians, setTechnicians] = useState([]);

  // Fetch and organize technician data on component mount
  useEffect(() => {
    const fetchTechnicians = async () => {
      const technicianData = await getTechnicians();
      if (technicianData) {
        setTechnicians(technicianData);
      }
    };
    fetchTechnicians();
  }, []);

  const [restDayConflict, setRestDayConflict] = useState([]);
  const [jobOrderConflict, setJobOrderConflict] = useState([]);

  useEffect(() => {
    const getDayOfWeek = (dateString) => {
      const date = new Date(dateString);
      const daysOfWeek = [
        "Sunday",
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday",
      ];
      return daysOfWeek[date.getDay()];
    };

    // Detect rest day conflicts
    const detectRestDayConflicts = () => {
      if (!jobOrderParameters.start_date) {
        setRestDayConflict([]);
        return;
      }

      const dayOfWeek = getDayOfWeek(jobOrderParameters.start_date);
      const conflicts = technicians
        .filter((technician) => technician.rest_day.includes(dayOfWeek))
        .map((technician) => technician.id);
      setRestDayConflict(conflicts);
    };

    // Detect job order schedule conflicts
    const detectJobOrderConflicts = () => {
      if (
        !jobOrderParameters.start_date ||
        !jobOrderParameters.start_time ||
        !jobOrderParameters.end_date ||
        !jobOrderParameters.end_time
      ) {
        setJobOrderConflict([]);
        return;
      }

      const startDateTime = new Date(
        `${jobOrderParameters.start_date}T${jobOrderParameters.start_time}`
      );
      const endDateTime = new Date(
        `${jobOrderParameters.end_date}T${jobOrderParameters.end_time}`
      );
      const jobOrderConflictSet = jobOrders.reduce((conflicts, existingJob) => {
        const existingStartDateTime = new Date(
          `${existingJob.start_date}T${existingJob.start_time}`
        );
        const existingEndDateTime = new Date(
          `${existingJob.end_date}T${existingJob.end_time}`
        );

        const isOverlap =
          (startDateTime >= existingStartDateTime &&
            startDateTime < existingEndDateTime) ||
          (endDateTime > existingStartDateTime &&
            endDateTime <= existingEndDateTime) ||
          (startDateTime <= existingStartDateTime &&
            endDateTime >= existingEndDateTime);

        if (isOverlap) {
          existingJob.technicians_mapping.forEach((technician) => {
            conflicts.add(technician.id);
          });
        }

        return conflicts;
      }, new Set());

      setJobOrderConflict([...jobOrderConflictSet]);
    };

    // Call detection functions
    detectRestDayConflicts();
    detectJobOrderConflicts();
  }, [
    jobOrderParameters.start_date,
    jobOrderParameters.start_time,
    jobOrderParameters.end_date,
    jobOrderParameters.end_time,
    technicians,
    jobOrders,
  ]);

  // Function to format date
  function formatDate(dateString) {
    try {
      const options = { year: 'numeric', month: 'long', day: 'numeric' };
      const date = new Date(dateString);

      // Check if the date is valid
      if (isNaN(date.getTime())) {
        throw new Error("Invalid date format");
      }

      return new Intl.DateTimeFormat('en-US', options).format(date);
    } catch (error) {
      console.error(error.message);
      return "Invalid Date"; // Fallback for invalid dates
    }
  }

  const formattedDate = formatDate(jobOrderParameters.job_date);

  const handleSendEmail = async () => {
    const generateAttachmentLinks = (links, hasTermiteControl) => {
      // Determine which keys to use for the links
      const baseUrl = `https://boaz-joborder-generated.s3.amazonaws.com/${jobOrder.id}`;
      return {
        reports_url: `${baseUrl}/${jobOrder.id}_report.pdf`,
        certificate_url: `${baseUrl}/${jobOrder.id}_certificate.pdf`,
        receipts_url: jobOrder.competencies.includes("termite_control")
          ? `${baseUrl}/${jobOrder.id}_final_receiptTermite.pdf`
          : `${baseUrl}/${jobOrder.id}_final_receipt.pdf`,
      };
    };

    // Generate the attachment links
    const attachmentLinks = generateAttachmentLinks();

    // Create the HTML list items from the links
    const attachmentLinksHTML = Object.entries(attachmentLinks)
      .map(([key, url]) => {
        const fileName = key
          .replace(/_url$/, "")
          .replace(/_/g, " ")
          .toUpperCase(); // Remove '_url' and format
        return `<li><a href="${url}" target="_blank">${fileName}</a></li>`;
      })
      .join("");

    const htmlBody = `
    <html>
      <head>
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <style>
          body {
            font-family: Arial, sans-serif;
            margin: 0;
            padding: 0;
          }
          .email-container {
            width: 100%;
            max-width: 600px;
            margin: 0 auto;
            padding: 20px;
            
            border: 1px solid #ddd;
          }
          .header, .footer {
            width: 100%;
            text-align: center;
            margin: 0;
            padding: 20px 0;
            
          }
          .content {
            margin: 20px 0;
          }
           .content img {
            max-width: 100%;
            height: auto;
            display: block;
            margin: 0 auto;
          }
        </style>
      </head>
      <body>
        <div class="email-container">
          <!-- Header Section -->
          <div class="header">
            <img src="https://boaz-email-images.s3.ap-southeast-1.amazonaws.com/header_boaz_transparent1.png" alt="Boaz Header" style="max-width: 100%; height: auto;">
          </div>

          <!-- Main Content -->
          <div class="content">
            <body>            
            <p>Sir/Ma'am,</p>
            <p></p>
            <p>Good day!</p>
            <p></p>
            <p>We are pleased to send you our Accomplishment Report for the visit done on  ${formattedDate}.</p>
            <p>We thank you and please let us know if have any questions.</p>
            <p></p>            
            <p>Sincerely,</p>
            <p>Boaz Pest Control - Accounts Team</p>
            <ul>
              ${attachmentLinksHTML}
            </ul>
            <hr />                     
          </body>            
          </div>

          <!-- Footer Section -->
          <a href="mailto:boazpestcontrol@gmail.com" style="text-decoration: none;">
          <div class="footer">
            <img src="https://boaz-email-images.s3.ap-southeast-1.amazonaws.com/footer_boaz_transparent1.png" alt="Boaz Footer" style="max-width: 100%; height: auto;">
            <hr />
            <footer>              
              <p>If you no longer wish to receive these emails, you can <a href="mailto:unsubscribe-boaz@maestrosystemsinc.com">unsubscribe here</a>.</p>
            </footer>
          </div>
          </a>
        </div>
      </body>
    </html>`;



    const lambdaPayload = {
      from: "boaz-billing@maestrosystemsinc.com",
      recipients: [jobOrder?.client?.contact?.email?.email],
      subject: `Service Report for Job Order ${jobOrder?.id}`,
      body: htmlBody,
    };

    try {
      await sendEmailQueue(lambdaPayload);
      console.log("Email sent successfully:", error);
      setSuccessMessage("Email sent successfully!");
    } catch (error) {
      console.error("Error sending email:", error);
      setErrorMessage("Failed to send email.");
    }
  };

  async function handleSubmit(e) {
    e.preventDefault();
    
    // Early return if jobOrder is not loaded
    if (!jobOrder || !jobOrder.id) {
      setErrorMessage("Job order data is not available. Please refresh the page.");
      return;
    }
    
    setIsLoading(true); // Set loading to true
    setIsSubmitting(true); // Hide links during submission

    try {
      const inspection_report = inspectionRows.map((row, rowIndex) => {
        const rowData = {};

        // Get the location
        rowData.location =
          document.querySelector(`input[name="location-${rowIndex}"]`)?.value ||
          "";

        // Collect all pest counts
        rowData.pests = inspectionPestColumns.reduce(
          (acc, pestType, colIndex) => {
            const pestValue =
              document.querySelector(
                `input[name="pest-${rowIndex}-${pestType}"]`
              )?.value || 0;
            acc[pestType] = parseInt(pestValue, 10) || 0;
            return acc;
          },
          {}
        );

        // Get the total
        rowData.total =
          document.querySelector(`input[name="total-${rowIndex}"]`)?.value || 0;

        // Get findings and recommendations
        rowData.findings =
          document.querySelector(`input[name="findings-${rowIndex}"]`)?.value ||
          "";
        rowData.recommendation =
          document.querySelector(`input[name="recommendation-${rowIndex}"]`)
            ?.value || "";

        return rowData;
      });

      const updatedJobOrderParameters = {
        ...jobOrderParameters,
        reminder_notes,
        service_fee,
        inspection_report,
        services,
        chemical_used,
        equipment_used,
        service_report_payment_status,
        service_report_payment_method,
        accomplishedSignature: accomplishedSignatureURL,
        acknowledgedSignature: acknowledgedSignatureURL,
        service_report_status: "Done",
      };

      const payload = {
        jobOrder: { ...jobOrder },
        updatedJobOrderParameters: { ...updatedJobOrderParameters },
      };
      // Convert booleans in the payload
      const convertedPayload = convertBooleans(payload);

      await putJobOrder(jobOrder.id, updatedJobOrderParameters);
      // await putClient(id, putClientParameters)
      //downloadJSON(convertedPayload, "editjobOrderParameters.json");

      setSuccessMessage("Service Report updated successfully!");
      //code for pdf
      const response = await submitDocumentFieldMapper(convertedPayload);
      console.log("Job order parameters submitted successfully:", response);
      await handleSendEmail();

    } catch (error) {
      console.error("Error updating Service Report:", error);
      setErrorMessage(
        `An error occurred while updating the Service Report: ${error.message}. Please check your network connection and try again.`
      );
    } finally {
      setIsLoading(false);
      setIsSubmitting(false); // Re-display links after submission
    }
  }

  return (

    <BaseFormWrapper
      titleIcon={<ReceiptLongIcon />}
      title="Operations"
      subTitle="Service Reports"
      subTitleRoute="/manage-service-reports"
      formTitle="Edit Service Report"
      successMessage={successMessage}
      setSuccessMessage={setSuccessMessage}
      errorMessage={errorMessage}
      setErrorMessage={setErrorMessage}
      isLoading={false}
    >
      <form onSubmit={handleSubmit} className="job-order-form">
        <Grid container>
          <Grid
            item
            xs={12}
            md={12}
            sx={{ display: "flex", flexDirection: "column" }}
          >
            <Card
              variant="outlined"
              sx={{ padding: "20px", marginBottom: "20px", flex: 1 }}
            >
              <CardHeader
                title={
                  <Typography
                    variant="h6"
                    sx={{
                      paddingBottom: "1rem",
                      fontWeight: "bold",
                      textTransform: "uppercase",
                      fontSize: "1rem",
                    }}
                  >
                    Basic Information
                  </Typography>
                }
              />
              <CardContent
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {" "}
                <Grid container spacing={2}>
                  <Grid item xs={12} md={12} lg={2}>
                    <TextField
                      name="id"
                      label="Job Order ID"
                      value={jobOrder?.id || ""}
                      fullWidth
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12} md={12} lg={2}>
                    <FormControl fullWidth>
                      <InputLabel id="job-order-status-label">
                        Job Order Status
                      </InputLabel>
                      <Select
                        labelId="job-order-status-label"
                        name="status"
                        value={jobOrder?.status || ""}
                        label="Job Order Status"
                        disabled
                      >
                        <MenuItem value="Not Done">Not Done</MenuItem>
                        <MenuItem value="Rescheduled">Rescheduled</MenuItem>
                        <MenuItem value="Done">Done</MenuItem>
                        <MenuItem value="Cancelled By Client">
                          Cancelled By Client
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      name="Client ID"
                      label="Client ID"
                      value={jobOrder?.client_id || ""}
                      fullWidth
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      name="Client Name"
                      label="Client Name"
                      value={jobOrder?.client?.name || ""}
                      fullWidth
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={12} lg={10}>
                        <TextField
                          label="Job Site Address"
                          variant="outlined"
                          fullWidth
                          value={
                            jobOrder?.client?.address
                              ? `${jobOrder?.client?.address?.house_unit_street || ""
                              }, ${jobOrder?.client?.address?.barangay || ""
                              }, ${jobOrder?.client?.address?.city || ""
                              }, ${jobOrder?.client?.address?.metro_province || ""} ${jobOrder?.client?.address?.zipcode || ""}`
                              : ""
                          }
                          disabled
                        />
                      </Grid>

                      <Grid item xs={12} md={12} lg={2}>
                        <TextField
                          name="area"
                          label="Area"
                          value={jobOrder?.client?.address?.area || ""}
                          fullWidth
                          disabled
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item xs={12} md={12} lg={4}>
                    <TextField
                      fullWidth
                      label="Contact Person"
                      variant="outlined"
                      value={
                        jobOrder?.client?.contact
                          ? `${jobOrder?.client?.contact?.lastname || ""}, ${jobOrder?.client?.contact?.firstname || ""
                          }`
                          : ""
                      } // Display the contact's full name or an empty string if not available
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12} md={12} lg={4}>
                    <TextField
                      fullWidth
                      label="Contact Email"
                      variant="outlined"
                      value={jobOrder.client?.contact?.email?.email || ""} // Display the contact email or an empty string if not available
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12} md={12} lg={4}>
                    <TextField
                      fullWidth
                      label="Contact Number"
                      variant="outlined"
                      value={
                        jobOrder.client?.contact?.number
                          ? `${jobOrder.client.contact.number.type}: ${jobOrder.client.contact.number.number}`
                          : ""
                      } // Display the contact number or an empty string if not available
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12} md={12} lg={4}>
                    <TextField
                      fullWidth
                      label="Category"
                      variant="outlined"
                      value={client?.category || ""}
                      disabled
                    />
                  </Grid>

                  <Grid item xs={12} md={12} lg={4}>
                    <TextField
                      fullWidth
                      label="Parent Client"
                      variant="outlined"
                      value={client?.parent_client?.name || ""}
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12} md={12} lg={4}>
                    <TextField
                      label="Location Type"
                      value={
                        client?.location_type
                          ? client.location_type
                          : ""
                      }
                      fullWidth
                      variant="outlined"
                      sx={{ fontSize: "0.875rem" }}
                      disabled
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* <Grid item xs={12}>
            <Card
              variant="outlined"
              sx={{ padding: "20px", marginBottom: "20px" }}
            >
              <CardHeader
                title={
                  <Typography
                    variant="h6"
                    sx={{
                      paddingBottom: "1rem",
                      fontWeight: "bold",
                      textTransform: "uppercase",
                      fontSize: "1rem",
                    }}
                  >
                    Job Type
                  </Typography>
                }
              />
              <CardContent>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={12} lg={12}>
                    <FormControl fullWidth>
                      <TextField
                        label="Job Type"
                        variant="outlined"
                        value={
                          jobOrder?.job_type
                            ? jobOrder.job_type.charAt(0).toUpperCase() +
                            jobOrder.job_type.slice(1)
                            : ""
                        }
                        disabled
                      />
                    </FormControl>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid> */}

          <Grid item xs={12}>
            <Card
              variant="outlined"
              style={{ overflowX: 'auto' }}
            >
              <CardHeader
                title={
                  <Typography
                  >
                    Job Type
                  </Typography>
                }
                sx={{
                  padding: 0
                }}
              />
              <CardContent
                sx={{
                  padding: "0 !important",
                  display: "grid",
                  gridTemplateColumns: "repeat(7, auto)",
                  gap: 1,
                }}
              >
                {job_types.map((option) => {
                  const formattedOption = option
                    .toLowerCase()
                    .replace(/\s+/g, "_");
                  return (
                    <FormControlLabel
                      key={formattedOption}
                      control={
                        <Checkbox
                          name={formattedOption}
                          checked={(jobOrder.job_types || []).includes(formattedOption)}
                          disabled
                        />
                      }
                      label={option}
                    />

                  );
                })}
              </CardContent>
            </Card>
          </Grid>

          <Grid
            item
            xs={12}
            md={12}
            sx={{ display: "flex", flexDirection: "column" }}
          >
            <Card
              variant="outlined"
              sx={{ padding: "20px", marginBottom: "20px", flex: 1 }}
            >
              <CardHeader
                title={
                  <Typography
                    variant="h6"
                    sx={{
                      paddingBottom: "1rem",
                      fontWeight: "bold",
                      textTransform: "uppercase",
                      fontSize: "1rem",
                    }}
                  >
                    Competency
                  </Typography>
                }
              />

              <CardContent
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      gap: 2,
                      flexWrap: "wrap",
                    }}
                  >
                    {competencies.map((option) => {
                      const formattedOption = option
                        .toLowerCase()
                        .replace(/\s+/g, "_");
                      return (
                        <FormControlLabel
                          key={formattedOption}
                          control={
                            <Checkbox
                              name={formattedOption}
                              checked={
                                jobOrder?.competencies?.includes(
                                  formattedOption
                                ) || false
                              }
                              disabled
                            />
                          }
                          label={option}
                        />
                      );
                    })}
                  </Box>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid
            item
            xs={6}
            md={6}
            sx={{ display: "flex", flexDirection: "column" }}
          >
            <Card
              variant="outlined"
              sx={{ padding: "20px", marginBottom: "20px", flex: 1 }}
            >
              <CardHeader
                title={
                  <Typography
                    variant="h6"
                    sx={{
                      paddingBottom: "1rem",
                      fontWeight: "bold",
                      textTransform: "uppercase",
                      fontSize: "1rem",
                    }}
                  >
                    Specialization - General Pest Control
                  </Typography>
                }
              />

              <CardContent
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      gap: 2,
                      flexWrap: "wrap",
                    }}
                  >
                    {specializationGPC.map((option) => {
                      const formattedOption = option
                        .toLowerCase()
                        .replace(/\s+/g, "_");
                      return (
                        <FormControlLabel
                          key={formattedOption}
                          control={
                            <Checkbox
                              name={formattedOption}
                              checked={
                                jobOrder?.specialization?.general_pest_control?.includes(
                                  formattedOption
                                ) || false
                              }
                              disabled
                            />
                          }
                          label={option}
                        />
                      );
                    })}
                  </Box>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid
            item
            xs={6}
            md={6}
            sx={{ display: "flex", flexDirection: "column" }}
          >
            <Card
              variant="outlined"
              sx={{ padding: "20px", marginBottom: "20px", flex: 1 }}
            >
              <CardHeader
                title={
                  <Typography
                    variant="h6"
                    sx={{
                      paddingBottom: "1rem",
                      fontWeight: "bold",
                      textTransform: "uppercase",
                      fontSize: "1rem",
                    }}
                  >
                    Specialization - Termite
                  </Typography>
                }
              />

              <CardContent
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      gap: 2,
                      flexWrap: "wrap",
                    }}
                  >
                    {specializationTermite.map((option) => {
                      const formattedOption = option
                        .toLowerCase()
                        .replace(/\s+/g, "_");
                      return (
                        <FormControlLabel
                          key={formattedOption}
                          control={
                            <Checkbox
                              name={formattedOption}
                              checked={
                                jobOrder?.specialization?.termite?.includes(
                                  formattedOption
                                ) || false
                              }
                              disabled
                            />
                          }
                          label={option}
                        />
                      );
                    })}
                  </Box>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
          <Grid
            item
            xs={12}
            md={12}
            sx={{ display: "flex", flexDirection: "column" }}
          >
            <Card
              variant="outlined"
              sx={{ padding: "20px", marginBottom: "20px", flex: 1 }}
            >
              <CardHeader
                title={
                  <Typography
                    variant="h6"
                    sx={{
                      paddingBottom: "1rem",
                      fontWeight: "bold",
                      textTransform: "uppercase",
                      fontSize: "1rem",
                    }}
                  >
                    Special Notes
                  </Typography>
                }
              />
              <CardContent sx={{ flex: 1 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <TextField
                      label="Special Notes"
                      name="special_notes"
                      type="text"
                      value={jobOrder.special_notes || ""}
                      fullWidth
                      disabled
                      multiline
                      rows={4}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
          <Grid
            item
            xs={12}
            md={12}
            sx={{ display: "flex", flexDirection: "column" }}
          >
            <Card
              variant="outlined"
              sx={{ padding: "20px", marginBottom: "20px", flex: 1 }}
            >
              <CardHeader
                title={
                  <Typography
                    variant="h6"
                    sx={{
                      paddingBottom: "1rem",
                      fontWeight: "bold",
                      textTransform: "uppercase",
                      fontSize: "1rem",
                    }}
                  >
                    Appointment
                  </Typography>
                }
              />
              <CardContent sx={{ flex: 1 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={12} lg={6}>
                    <FormControl fullWidth>
                      <InputLabel id="appointment-type-label">
                        Appointment Type
                      </InputLabel>
                      <Select
                        labelId="appointment-type-label"
                        name="appointment_type"
                        value={jobOrderParameters?.appointment_type || ""}
                        label="Appointment Type"
                        onChange={(e) =>
                          handleJobOrderChange?.(
                            "appointment_type",
                            e.target.value
                          )
                        }
                      >
                        <MenuItem value="Initial">Initial</MenuItem>
                        <MenuItem value="Follow-up">Follow-up</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={12} lg={6}>
                    <FormControl fullWidth>
                      <InputLabel id="treatment-type-label">
                        Treatment Type
                      </InputLabel>
                      <Select
                        labelId="treatment-type-label"
                        name="treatment_type"
                        value={jobOrderParameters.treatment_type}
                        label="treatment_type"
                        onChange={(e) =>
                          handleJobOrderChange("treatment_type", e.target.value)
                        }
                      >
                        <MenuItem value="QB">QB</MenuItem>
                        <MenuItem value="FFUP">FFUP</MenuItem>
                        <MenuItem value="LT">LT</MenuItem>
                        <MenuItem value="QBLT">QBLT</MenuItem>
                        <MenuItem value="Backjob">Backjob</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid
            item
            xs={12}
            md={12}
            sx={{ display: "flex", flexDirection: "column" }}
          >
            <Card
              variant="outlined"
              sx={{ padding: "20px", marginBottom: "20px", flex: 1 }}
            >
              <CardContent sx={{ flex: 1 }}>
                <Grid item xs={12}>
                  <TableContainer sx={{ overflowX: "auto" }}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell
                            align="center"
                            colSpan={8}
                            sx={{ fontWeight: "bold" }}
                          >
                            Inspection and Pest Count
                          </TableCell>
                          <TableCell align="center" sx={{ fontWeight: "bold" }}>
                            Findings
                          </TableCell>
                          <TableCell align="center" sx={{ fontWeight: "bold" }}>
                            Recommendation
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            align="center"
                            rowSpan={2}
                            sx={{ fontWeight: "bold" }}
                          >
                            Location
                          </TableCell>
                          <TableCell
                            align="center"
                            colSpan={inspectionPestColumns.length}
                            sx={{ fontWeight: "bold" }}
                          >
                            Pests
                          </TableCell>
                          <TableCell
                            align="center"
                            rowSpan={2}
                            sx={{ fontWeight: "bold" }}
                          >
                            Total
                          </TableCell>
                          <TableCell align="center" rowSpan={2}>
                            (Pls write possible root cause or Refer to Legend
                            below)
                          </TableCell>
                          <TableCell align="center" rowSpan={2}>
                            (Pls write client's action or Pls refer to Legend
                            below)
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          {inspectionPestColumns.map((pest, index) => (
                            <TableCell
                              key={index}
                              align="center"
                              sx={{ fontWeight: "bold" }}
                            >
                              {pest}
                            </TableCell>
                          ))}
                          <TableCell sx={{ fontWeight: "bold" }} />
                          <TableCell sx={{ fontWeight: "bold" }} />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {inspectionReport?.map((report, rowIndex) => (
                          <TableRow key={rowIndex}>
                            <TableCell>
                              <TextField
                                variant="outlined"
                                size="small"
                                fullWidth
                                name={`location-${rowIndex}`}
                                value={report?.location || ""}
                                onChange={(e) => {
                                  const updatedReport = [...inspectionReport];
                                  updatedReport[rowIndex] = {
                                    ...report,
                                    location: e.target.value,
                                  };
                                  setInspectionReport(updatedReport);
                                }}
                                sx={{ minWidth: 150 }}
                              />
                            </TableCell>

                            {inspectionPestColumns?.map(
                              (pestType, colIndex) => (
                                <TableCell key={colIndex}>
                                  <TextField
                                    fullWidth
                                    variant="outlined"
                                    size="small"
                                    type="number"
                                    value={report?.pests?.[pestType] || ""}
                                    onChange={(e) => {
                                      const updatedReport = [
                                        ...inspectionReport,
                                      ];
                                      updatedReport[rowIndex] = {
                                        ...report,
                                        pests: {
                                          ...report?.pests,
                                          [pestType]:
                                            parseInt(e.target.value, 10) || 0,
                                        },
                                      };
                                      setInspectionReport(updatedReport);
                                    }}
                                    InputProps={{
                                      inputProps: { min: 0, step: 1 },
                                    }} // Ensure whole numbers only
                                    name={`pest-${rowIndex}-${pestType}`}
                                    sx={{ minWidth: 50 }}
                                  />
                                </TableCell>
                              )
                            )}

                            <TableCell>
                              <TextField
                                variant="outlined"
                                size="small"
                                value={Object.values(
                                  report?.pests || {}
                                ).reduce(
                                  (sum, value) =>
                                    sum + parseInt(value || 0, 10),
                                  0
                                )}
                                fullWidth
                                disabled
                                sx={{ minWidth: 50 }}
                              />
                            </TableCell>

                            <TableCell>
                              <TextField
                                variant="outlined"
                                size="small"
                                fullWidth
                                name={`findings-${rowIndex}`}
                                value={report?.findings || ""}
                                onChange={(e) => {
                                  const updatedReport = [...inspectionReport];
                                  updatedReport[rowIndex] = {
                                    ...report,
                                    findings: e.target.value,
                                  };
                                  setInspectionReport(updatedReport);
                                }}
                                sx={{ minWidth: 150 }}
                              />
                            </TableCell>

                            <TableCell>
                              <TextField
                                variant="outlined"
                                size="small"
                                fullWidth
                                name={`recommendation-${rowIndex}`}
                                value={report?.recommendation || ""}
                                onChange={(e) => {
                                  const updatedReport = [...inspectionReport];
                                  updatedReport[rowIndex] = {
                                    ...report,
                                    recommendation: e.target.value,
                                  };
                                  setInspectionReport(updatedReport);
                                }}
                                sx={{ minWidth: 150 }}
                              />
                            </TableCell>
                          </TableRow>
                        ))}

                        <TableRow>
                          <TableCell sx={{ fontWeight: "bold" }}>
                            Overall Total Pests
                          </TableCell>
                          {inspectionPestColumns?.map((pestType, colIndex) => (
                            <TableCell key={colIndex}>
                              <TextField
                                variant="outlined"
                                size="small"
                                value={inspectionReport?.reduce(
                                  (sum, report) =>
                                    sum +
                                    parseInt(
                                      report?.pests?.[pestType] || 0,
                                      10
                                    ),
                                  0
                                )}
                                fullWidth
                                disabled
                                sx={{ minWidth: 50 }}
                              />
                            </TableCell>
                          ))}

                          <TableCell sx={{ fontWeight: "bold" }}>
                            <TextField
                              variant="outlined"
                              size="small"
                              value={inspectionReport?.reduce(
                                (grandTotal, report) => {
                                  return (
                                    grandTotal +
                                    Object.values(report?.pests || {}).reduce(
                                      (sum, value) =>
                                        sum + parseInt(value || 0, 10),
                                      0
                                    )
                                  );
                                },
                                0
                              )}
                              fullWidth
                              disabled
                              sx={{ minWidth: 50 }}
                            />
                          </TableCell>

                          <TableCell colSpan={2} />
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid
            item
            xs={12}
            md={12}
            sx={{ display: "flex", flexDirection: "column" }}
          >
            <Card
              variant="outlined"
              sx={{ padding: "20px", marginBottom: "20px", flex: 1 }}
            >
              <CardContent sx={{ flex: 1 }}>
                <Grid item xs={12}>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell
                            align="center"
                            colSpan={2}
                            style={{ fontWeight: "bold" }}
                          >
                            Legend / Code
                          </TableCell>
                          <TableCell
                            align="center"
                            colSpan={2}
                            style={{ fontWeight: "bold" }}
                          >
                            Activities Done
                          </TableCell>
                          <TableCell
                            align="center"
                            colSpan={2}
                            style={{ fontWeight: "bold" }}
                          >
                            Services Performed
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell style={{ fontWeight: "bold" }}>
                            Findings
                          </TableCell>
                          <TableCell style={{ fontWeight: "bold" }}>
                            Recommendations
                          </TableCell>
                          <TableCell align="center" colSpan={4}>
                            (Please check beside)
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {legendTable?.map((legend, index) => (
                          <TableRow key={`legend-${index}`}>
                            <TableCell>{legend?.finding || ""}</TableCell>
                            <TableCell>
                              {legend?.recommendation || ""}
                            </TableCell>
                            <TableCell>
                              {services?.[index]?.activity || ""}
                            </TableCell>
                            <TableCell>
                              <Checkbox
                                checked={
                                  services?.[index]?.activityChecked || false
                                }
                                onChange={() =>
                                  handleServicesCheckboxChange(
                                    services?.[index]?.id,
                                    "activityChecked"
                                  )
                                }
                                disabled={!services?.[index]}
                              />
                            </TableCell>
                            <TableCell>
                              {services?.[index]?.service || ""}
                            </TableCell>
                            <TableCell>
                              <Checkbox
                                checked={
                                  services?.[index]?.serviceChecked || false
                                }
                                onChange={() =>
                                  handleServicesCheckboxChange(
                                    services?.[index]?.id,
                                    "serviceChecked"
                                  )
                                }
                                disabled={!services?.[index]}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid
            item
            xs={12}
            md={12}
            sx={{ display: "flex", flexDirection: "column" }}
          >
            <Card
              variant="outlined"
              sx={{ padding: "20px", marginBottom: "20px", flex: 1 }}
            >
              <CardContent sx={{ flex: 1 }}>
                <Grid item xs={12}>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell
                            align="center"
                            colSpan={2}
                            style={{ fontWeight: "bold" }}
                          >
                            Chemical Used / Batch # / Expiration
                          </TableCell>
                          <TableCell
                            align="center"
                            style={{ fontWeight: "bold" }}
                          >
                            Dilution
                          </TableCell>
                          <TableCell
                            align="center"
                            colSpan={2}
                            style={{ fontWeight: "bold" }}
                          >
                            Equipment Used
                          </TableCell>
                          <TableCell
                            align="center"
                            style={{ fontWeight: "bold" }}
                          >
                            Reminder Notes
                          </TableCell>
                          <TableCell
                            align="center"
                            colSpan={4}
                            style={{ fontWeight: "bold" }}
                          >
                            Service Fee
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {chemical_used?.map((chemical, index) => (
                          <TableRow key={index}>
                            <TableCell>{chemical?.label || ""}:</TableCell>
                            <TableCell>
                              <TextField
                                fullWidth
                                variant="outlined"
                                size="small"
                                value={chemical?.chemical || ""}
                                onChange={(e) => {
                                  const updatedChemicalUsed = [
                                    ...chemical_used,
                                  ];
                                  updatedChemicalUsed[index] = {
                                    ...chemical,
                                    chemical: e.target.value,
                                  };
                                  setChemicalUsed(updatedChemicalUsed);
                                }}
                                sx={{ minWidth: 100 }}
                              />
                            </TableCell>
                            <TableCell>
                              <TextField
                                fullWidth
                                variant="outlined"
                                size="small"
                                value={chemical?.dilution || ""}
                                onChange={(e) => {
                                  const updatedChemicalUsed = [
                                    ...chemical_used,
                                  ];
                                  updatedChemicalUsed[index] = {
                                    ...chemical,
                                    dilution: e.target.value,
                                  };
                                  setChemicalUsed(updatedChemicalUsed);
                                }}
                                sx={{ minWidth: 50 }}
                              />
                            </TableCell>

                            {equipment_used?.[index] ? (
                              <>
                                <TableCell>
                                  {equipment_used[index]?.label || ""}
                                </TableCell>
                                <TableCell>
                                  <Checkbox
                                    checked={
                                      equipment_used[index]?.usage || false
                                    }
                                    onChange={() => {
                                      const updatedEquipmentUsed = [
                                        ...equipment_used,
                                      ];
                                      updatedEquipmentUsed[index] = {
                                        ...equipment_used[index],
                                        usage:
                                          !updatedEquipmentUsed[index]?.usage,
                                      };
                                      setEquipmentUsed(updatedEquipmentUsed);
                                    }}
                                  />
                                </TableCell>
                              </>
                            ) : (
                              <>
                                <TableCell></TableCell>
                                <TableCell></TableCell>
                              </>
                            )}

                            {index === 0 && (
                              <TableCell rowSpan={6}>
                                <TextField
                                  fullWidth
                                  variant="outlined"
                                  value={reminder_notes || ""}
                                  onChange={(e) => {
                                    setReminderNotes(e.target.value);
                                    setjobOrderParameters((prev) => ({
                                      ...prev,
                                      reminder_notes: e.target.value,
                                    }));
                                  }}
                                  multiline
                                  rows={16}
                                  sx={{ minWidth: 150 }}
                                />
                              </TableCell>
                            )}

                            {index === 0 && (
                              <TableCell rowSpan={2} colSpan={4}>
                                <TextField
                                  variant="outlined"
                                  value={service_fee || ""}
                                  onChange={(e) => {
                                    setServiceFee(e.target.value);
                                    setjobOrderParameters((prev) => ({
                                      ...prev,
                                      service_fee: e.target.value,
                                    }));
                                  }}
                                  size="small"
                                  fullWidth
                                  InputProps={{
                                    startAdornment: (
                                      <InputAdornment position="start">
                                        ₱
                                      </InputAdornment>
                                    ),
                                  }}
                                />
                              </TableCell>
                            )}

                            {index === 2 && (
                              <TableCell align="center" colSpan={4}>
                                Payment Status:
                              </TableCell>
                            )}

                            {index === 3 && (
                              <>
                                <TableCell>
                                  {service_report_payment_status?.[0]?.label ||
                                    ""}
                                </TableCell>
                                <TableCell>
                                  <Checkbox
                                    checked={
                                      service_report_payment_status?.[0]
                                        ?.selection || false
                                    }
                                    onChange={() =>
                                      handlePaymentStatusChange(0)
                                    }
                                  />
                                </TableCell>
                                <TableCell>
                                  {service_report_payment_method?.[0]?.label || ""}
                                </TableCell>
                                <TableCell>
                                  <Checkbox
                                    checked={
                                      service_report_payment_method?.[0]?.selection || false
                                    }
                                    onChange={() =>
                                      handlePaymentMethodChange(0)
                                    }
                                  />
                                </TableCell>
                              </>
                            )}

                            {index === 4 && (
                              <>
                                <TableCell>
                                  {service_report_payment_status?.[1]?.label ||
                                    ""}
                                </TableCell>
                                <TableCell>
                                  <Checkbox
                                    checked={
                                      service_report_payment_status?.[1]
                                        ?.selection || false
                                    }
                                    onChange={() =>
                                      handlePaymentStatusChange(1)
                                    }
                                  />
                                </TableCell>
                                <TableCell>
                                  {service_report_payment_method?.[1]?.label || ""}
                                </TableCell>
                                <TableCell>
                                  <Checkbox
                                    checked={
                                      service_report_payment_method?.[1]?.selection || false
                                    }
                                    onChange={() =>
                                      handlePaymentMethodChange(1)
                                    }
                                  />
                                </TableCell>
                              </>
                            )}

                            {index === 5 && (
                              <>
                                <TableCell>
                                  {service_report_payment_status?.[2]?.label ||
                                    ""}
                                </TableCell>
                                <TableCell>
                                  <Checkbox
                                    checked={
                                      service_report_payment_status?.[2]
                                        ?.selection || false
                                    }
                                    onChange={() =>
                                      handlePaymentStatusChange(2)
                                    }
                                  />
                                </TableCell>
                                <TableCell>
                                  {service_report_payment_method?.[2]?.label || ""}
                                </TableCell>
                                <TableCell>
                                  <Checkbox
                                    checked={
                                      service_report_payment_method?.[2]?.selection || false
                                    }
                                    onChange={() =>
                                      handlePaymentMethodChange(2)
                                    }
                                  />
                                </TableCell>
                              </>
                            )}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid
            item
            xs={12}
            md={12}
            sx={{ display: "flex", flexDirection: "column" }}
          >
            <Card
              variant="outlined"
              sx={{
                padding: "20px",
                marginBottom: "20px",
                flex: 1,
              }}
            >
              <CardHeader
                title={
                  <Typography
                    variant="h6"
                    sx={{
                      paddingBottom: "1rem",
                      fontWeight: "bold",
                      textTransform: "uppercase",
                      fontSize: "1rem",
                    }}
                  >
                    Schedule
                  </Typography>
                }
              />
              <CardContent sx={{ flex: 1 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Start Date"
                      name="job_date"
                      type="date"
                      value={jobOrder?.job_date || ""}
                      onChange={(e) =>
                        handleJobOrderChange?.("job_date", e.target.value)
                      }
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Start Time"
                      name="start_time"
                      type="time"
                      value={jobOrder?.start_time || ""}
                      onChange={(e) =>
                        handleJobOrderChange?.("start_time", e.target.value)
                      }
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <TextField
                      label="End Time"
                      name="end_time"
                      type="time"
                      value={jobOrder?.end_time || ""}
                      onChange={(e) =>
                        handleJobOrderChange?.("end_time", e.target.value)
                      }
                      fullWidth
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid
            item
            xs={12}
            md={12}
            sx={{ display: "flex", flexDirection: "column" }}
          >
            <Card
              variant="outlined"
              sx={{ padding: "20px", marginBottom: "20px", flex: 1 }}
            >
              <CardHeader
                title={
                  <Typography
                    variant="h6"
                    sx={{
                      paddingBottom: "1rem",
                      fontWeight: "bold",
                      textTransform: "uppercase",
                      fontSize: "1rem",
                    }}
                  >
                    Technicians
                  </Typography>
                }
              />
              <CardContent
                sx={{
                  flex: 1,
                }}
              >
                <Grid container spacing={3}>
                  {jobOrder?.technicians?.length > 0 ? (
                    jobOrder?.technicians?.map((technician, index) => (
                      <Grid
                        item
                        xs={12}
                        md={12}
                        lg={6}
                        key={technician?.id || index}
                      >
                        <TextField
                          label={`Technician ${index + 1}`}
                          variant="outlined"
                          fullWidth
                          value={`${technician?.firstname || ""} ${technician?.lastname || ""
                            }`}
                          disabled
                        />
                      </Grid>
                    ))
                  ) : (
                    <Grid item xs={12} md={12} lg={6}>
                      <TextField
                        label="Technician"
                        variant="outlined"
                        fullWidth
                        value="No Technicians Available"
                        InputProps={{
                          readOnly: true,
                        }}
                        InputLabelProps={{
                          sx: { fontSize: "0.875rem" },
                        }}
                      />
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid container spacing={3}>

            {/* Accomplished Signature Section */}
            <Grid item xs={12} md={6}>
              <Card variant="outlined"
                sx={{ padding: "20px", marginBottom: "20px", marginLeft: "25px", flex: 1 }}
              >
                <CardHeader title={<Typography
                  variant="h6"
                  sx={{
                    paddingBottom: "1rem",
                    fontWeight: "bold",
                    textTransform: "uppercase",
                    fontSize: "1rem",
                  }}
                >
                  Accomplished Signature
                </Typography>
                } />
                <CardContent>
                  {accomplishedSignatureURL && (
                    <img
                      src={accomplishedSignatureURL}
                      alt="Accomplished Signature"
                      style={{ width: "80%", height: "90px", objectFit: "contain", marginBottom: "10px" }}
                    />
                  )}
                  <SignatureCanvas
                    ref={accomplishedSignatureRef}
                    penColor="black"
                    canvasProps={{
                      width: 400,
                      height: 150,
                      className: "sigCanvas",
                      style: { border: "1px solid #ccc", borderRadius: "5px" },
                    }}
                  />

                  <Grid container spacing={3} sx={{ marginTop: 2 }}>
                    <Grid item xs={12} md={12} lg={12}>
                      <FormControl fullWidth>
                        <InputLabel id="accomplished-by-technician-label">
                          Technician
                        </InputLabel>
                        <Select
                          labelId="accomplished-by-technician-label"
                          name="accomplished-by-technician"
                          value={
                            jobOrderParameters.accomplished_by_technician
                          }
                          label="Technician"
                          onChange={(e) =>
                            handleJobOrderChange(
                              "accomplished_by_technician",
                              e.target.value
                            )
                          }
                        >

                          {jobOrder?.technicians?.map((technician, index) => (
                            <MenuItem
                              key={index}
                              value={`${technician?.firstname} ${technician?.lastname}`}
                            >
                              {technician?.firstname} {technician?.lastname}
                            </MenuItem>


                          ))}

                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                  <Box display="flex" justifyContent="flex-start" gap={2} marginTop={2}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => saveSignature(accomplishedSignatureRef, "accomplished")}
                      style={{ marginTop: "10px", marginRight: "10px" }}

                    >
                      Save
                    </Button>
                    <Button
                      variant="outlined"
                      color="secondary"
                      onClick={() => clearSignature(accomplishedSignatureRef, "accomplished")}
                      style={{ marginTop: "10px" }}

                    >
                      Clear
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            {/* Acknowledged Signature Section */}
            <Grid item xs={12} md={6}>
              <Card variant="outlined"
                sx={{ padding: "20px", marginBottom: "20px", flex: 1 }}
              >
                <CardHeader title={<Typography
                  variant="h6"
                  sx={{
                    paddingBottom: "1rem",
                    fontWeight: "bold",
                    textTransform: "uppercase",
                    fontSize: "1rem",
                  }}
                >
                  Acknowledged Signature
                </Typography>
                } />
                <CardContent>
                  {acknowledgedSignatureURL && (
                    <img
                      src={acknowledgedSignatureURL}
                      alt="Acknowledged Signature"
                      style={{ width: "80%", height: "90px", objectFit: "contain", marginBottom: "10px" }}
                    />
                  )}
                  <SignatureCanvas
                    ref={acknowledgedSignatureRef}
                    penColor="black"
                    canvasProps={{
                      width: 400,
                      height: 150,
                      className: "sigCanvas",
                      style: { border: "1px solid #ccc", borderRadius: "5px" },
                    }}
                  />
                  <Grid container spacing={3} sx={{ marginTop: 2 }}>
                    <Grid item xs={12} md={12} lg={12}>
                      <TextField
                        fullWidth
                        label="Contact Person"
                        name="Contact Person"
                        variant="outlined"
                        value={
                          jobOrder?.client?.contact
                            ? `${jobOrder?.client?.contact.firstname || ""} ${jobOrder?.client?.contact.lastname || ""
                            }`
                            : ""
                        }
                        disabled
                      />
                    </Grid>
                  </Grid>
                  <Box display="flex" justifyContent="flex-start" gap={2} marginTop={2}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => saveSignature(acknowledgedSignatureRef, "acknowledged")}
                      style={{ marginTop: "10px", marginRight: "10px" }}

                    >
                      Save
                    </Button>
                    <Button
                      variant="outlined"
                      color="secondary"
                      onClick={() => clearSignature(acknowledgedSignatureRef, "acknowledged")}
                      style={{ marginTop: "10px" }}
                    >
                      Clear
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>


          </Grid>


          {/* Display Receipts and Reports Links */}
          <Grid item xs={12}>
            <Card
              variant="outlined"
              sx={{ padding: "20px", marginBottom: "20px", flex: 1 }}
            >
              <CardHeader
                title={
                  <Typography
                    variant="h6"
                    sx={{
                      paddingBottom: "1rem",
                      fontWeight: "bold",
                      textTransform: "uppercase",
                      fontSize: "1rem",
                    }}
                  >
                    Document Links
                  </Typography>
                }
              />
              <CardContent sx={{ flex: 1 }}>
                <Grid container spacing={3}>
                  {!isSubmitting && ( // Hide links when submitting
                    jobOrder?.receipts_url ||
                      jobOrder?.receipt_termite_url ||
                      jobOrder?.certificate_url ||
                      jobOrder?.reports_url ? (
                      <>
                        {jobOrder?.receipts_url && (
                          <Grid item xs={12}>
                            <Box>
                              <Link
                                href={jobOrder.receipts_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                  textDecoration: "none",
                                  color: "primary.main",
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                Receipts.pdf{" "}
                                <OpenInNewIcon
                                  sx={{
                                    marginLeft: 1,
                                    fontSize: "1rem",
                                  }}
                                />
                              </Link>
                            </Box>
                          </Grid>
                        )}

                        {jobOrder?.receipt_termite_url && (
                          <Grid item xs={12}>
                            <Box>
                              <Link
                                href={jobOrder.receipt_termite_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                  textDecoration: "none",
                                  color: "primary.main",
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                Receipt_Termite.pdf
                                <OpenInNewIcon
                                  sx={{
                                    marginLeft: 1,
                                    fontSize: "1rem",
                                  }}
                                />
                              </Link>
                            </Box>
                          </Grid>
                        )}

                        {jobOrder?.certificate_url && (
                          <Grid item xs={12}>
                            <Box>
                              <Link
                                href={jobOrder.certificate_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                  textDecoration: "none",
                                  color: "primary.main",
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                Certificate.pdf
                                <OpenInNewIcon
                                  sx={{
                                    marginLeft: 1,
                                    fontSize: "1rem",
                                  }}
                                />
                              </Link>
                            </Box>
                          </Grid>
                        )}

                        {jobOrder?.reports_url && (
                          <Grid item xs={12}>
                            <Box>
                              <Link
                                href={jobOrder.reports_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                  textDecoration: "none",
                                  color: "primary.main",
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                Reports.pdf
                                <OpenInNewIcon
                                  sx={{
                                    marginLeft: 1,
                                    fontSize: "1rem",
                                  }}
                                />
                              </Link>
                            </Box>
                          </Grid>
                        )}
                      </>
                    ) : (
                      <Grid item xs={12}>
                        <Typography variant="body1" color="textSecondary">
                          No document links available.
                        </Typography>
                      </Grid>
                    )
                  )}
                </Grid>
              </CardContent>

            </Card>
          </Grid>

          {/* Form buttons */}
          <Grid
            item
            xs={12}
            display="flex"
            justifyContent="flex-end"
            alignItems="center"
            mt={2}
            mb={2}
            mr={2}
          >
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => {
                if (returnTo) {
                  // Use window.location.href to force a full page navigation
                  window.location.href = returnTo;
                } else {
                  navigate("/manage-service-reports");
                }
              }}
              sx={{
                fontSize: "0.875rem",
                color: "#FF4C4C",
                borderColor: "#FF4C4C",
              }}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              sx={{
                marginLeft: "10px",
                fontSize: "0.875rem",
              }}
              disabled={isLoading}
            >
              Submit
            </Button>
          </Grid>
        </Grid>
      </form>
    </BaseFormWrapper>
  );
}

export default EditServiceReport;
